import React from 'react';
import PubSubsManager from '@classes/PubSubsManager';
import EventConverter from '@classes/StreamEvents/WSEventConverter';
import EventUpdateTracker from '@API/global_event_inbox/EventUpdateTracker';
import { isEmpty, isEqual } from 'lodash';
import { EventsFilterParams } from '@classes/StreamEvents/EventsFilter';

type WebsocketConnectionsProps = {
  onMessage: (message: VideoStreamEvent) => void;
  onUpdateMessage: (message: VideoStreamEvent) => void;
  onMutedMessage: (message: MutedEvent) => void;
  onEventSelectionChange: (message: any) => void;
  streams: VideoStream[];
  company?: Company;
  companies?: Company[];
  connectToLiveEvents: boolean;
  filters: EventsFilterParams | undefined;
}

const WebsocketConnections: React.FC<WebsocketConnectionsProps> = (props) => {
  const { onMessage, onUpdateMessage, company, companies, streams, connectToLiveEvents, onMutedMessage, onEventSelectionChange, filters } = props;

  const companyRef = React.useRef(company);
  const companiesRef = React.useRef(companies);
  const prevCompaniesRef = React.useRef<Company[]>(companies || []);
  const connectToLiveEventsRef = React.useRef(connectToLiveEvents)
  const streamsRef = React.useRef(streams);
  const onMessageRef = React.useRef(onMessage);
  const onUpdateMessageRef = React.useRef(onUpdateMessage);
  const onMutedMessageRef = React.useRef(onMutedMessage)
  const onEventSelectionChangeRef = React.useRef(onEventSelectionChange);

  const getEffectiveCompanies = React.useCallback((): Company[] => {
    if (companyRef.current) return [companyRef.current];
    return companiesRef.current || [];
  }, [])

  const connectWebsockets = React.useCallback(() => {
    const effectiveCompanies = getEffectiveCompanies();

    effectiveCompanies.forEach((c) => {
      PubSubsManager.instance.subscribeToCompanyUpdates(c, (message: GlobalEvent) => {
        const converter: EventConverter = new EventConverter();
        const event: VideoStreamEvent = converter.convertFromDynamoDB(message);

        if (EventUpdateTracker.has(event)) {
          EventUpdateTracker.unregister(event);
          return;
        }

        onUpdateMessageRef.current(event);
      })
    })

    PubSubsManager.instance.subscribeToMutedStreamsUpdates((message: MutedEvent) => {
      onMutedMessageRef.current(message);
    })

    PubSubsManager.instance.subscribeToEventSelectionUpdates((message: any) => {
      onEventSelectionChangeRef.current(message);
    })
  }, [getEffectiveCompanies])

  React.useEffect(() => { onEventSelectionChangeRef.current = onEventSelectionChange }, [onEventSelectionChange])
  React.useEffect(() => { onMutedMessageRef.current = onMutedMessage }, [onMutedMessage])
  React.useEffect(() => { onUpdateMessageRef.current = onUpdateMessage }, [onUpdateMessage])
  React.useEffect(() => { onMessageRef.current = onMessage }, [onMessage])
  React.useEffect(() => { streamsRef.current = streams }, [streams])
  React.useEffect(() => { connectToLiveEventsRef.current = connectToLiveEvents }, [connectToLiveEvents])
  React.useEffect(() => { companyRef.current = company }, [company])
  React.useEffect(() => { companiesRef.current = companies }, [companies])
  React.useEffect(() => {
    connectWebsockets();

    return () => {
      disconectWebsockets();
    }
  }, [connectWebsockets]);
  React.useEffect(handleWebsocketChange, [connectToLiveEvents, filters])

  // Handle companies list changes (multi-company mode) - diff old vs new and re-subscribe
  React.useEffect(() => {
    if (company) return; // Single company mode - skip
    const prev = prevCompaniesRef.current;
    const next = companies || [];

    const prevIds = new Set(prev.map(c => c.companyId));
    const nextIds = new Set(next.map(c => c.companyId));

    // Unsubscribe removed companies
    prev.filter(c => !nextIds.has(c.companyId)).forEach(c => {
      PubSubsManager.instance.unsubscribeFromCompany(c);
      PubSubsManager.instance.unsubscribeToCompanyUpdates(c);
    });

    // Subscribe new companies
    const added = next.filter(c => !prevIds.has(c.companyId));
    if (added.length > 0) {
      added.forEach((c) => {
        PubSubsManager.instance.subscribeToCompanyUpdates(c, (message: GlobalEvent) => {
          const converter: EventConverter = new EventConverter();
          const event: VideoStreamEvent = converter.convertFromDynamoDB(message);

          if (EventUpdateTracker.has(event)) {
            EventUpdateTracker.unregister(event);
            return;
          }

          onUpdateMessageRef.current(event);
        })
      })

      // Re-run websocket change handler for new subscriptions
      handleWebsocketChange();
    }

    prevCompaniesRef.current = next;
  }, [companies, company])

  function subscribeCompanyToEvents(c: Company, handler: (message: WebsocketVideoStreamEvent) => void) {
    if (filters?.motion.enable) {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "motion")
      if (filters.sites?.sites && filters.sites?.sites.length > 5) PubSubsManager.instance.subscribeToCompanyByEventType(c, "motion", handler)
      else if (filters.sites?.sites) filters.sites.sites.forEach((siteId) => PubSubsManager.instance.subscribeToSiteByEventType(c, siteId, "motion", handler))
      else PubSubsManager.instance.subscribeToCompanyByEventType(c, "motion", handler)
    } else {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "motion")
    }

    if (filters?.people.enable) {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "person-detected")
      if (filters.sites?.sites && filters.sites?.sites.length > 5) PubSubsManager.instance.subscribeToCompanyByEventType(c, "person-detected", handler)
      else if (filters.sites?.sites) filters.sites.sites.forEach((siteId) => PubSubsManager.instance.subscribeToSiteByEventType(c, siteId, "person-detected", handler))
      else PubSubsManager.instance.subscribeToCompanyByEventType(c, "person-detected", handler)
    } else {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "person-detected")
    }

    if (filters?.vehicles.enable) {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "vehicle-detected")
      if (filters.sites?.sites && filters.sites?.sites.length > 5) PubSubsManager.instance.subscribeToCompanyByEventType(c, "vehicle-detected", handler)
      else if (filters.sites?.sites) filters.sites.sites.forEach((siteId) => PubSubsManager.instance.subscribeToSiteByEventType(c, siteId, "vehicle-detected", handler))
      else PubSubsManager.instance.subscribeToCompanyByEventType(c, "vehicle-detected", handler)
    } else {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "vehicle-detected")
    }

    if (filters?.safety.enable) {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "safety-infraction")
      if (filters.sites?.sites && filters.sites?.sites.length > 5) PubSubsManager.instance.subscribeToCompanyByEventType(c, "safety-infraction", handler)
      else if (filters.sites?.sites) filters.sites.sites.forEach((siteId) => PubSubsManager.instance.subscribeToSiteByEventType(c, siteId, "safety-infraction", handler))
      else PubSubsManager.instance.subscribeToCompanyByEventType(c, "safety-infraction", handler)
    } else {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "safety-infraction")
    }

    if (filters?.zones.enable) {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "geofence-triggered")
      if (filters.sites?.sites && filters.sites?.sites.length > 5) PubSubsManager.instance.subscribeToCompanyByEventType(c, "geofence-triggered", handler)
      else if (filters.sites?.sites) filters.sites.sites.forEach((siteId) => PubSubsManager.instance.subscribeToSiteByEventType(c, siteId, "geofence-triggered", handler))
      else PubSubsManager.instance.subscribeToCompanyByEventType(c, "geofence-triggered", handler)
    } else {
      PubSubsManager.instance.unsubscribeToCompanyEventsByType(c, "geofence-triggered")
    }
  }

  function handleWebsocketChange() {
    const effectiveCompanies = getEffectiveCompanies();
    const isMultiCompany = !companyRef.current;

    if (effectiveCompanies.length > 0) {
      if (connectToLiveEventsRef.current === true) {
        const handler = (message: WebsocketVideoStreamEvent) => {
          if (message.l && !isEmpty(message.l)) console.log(message.l);
          if (isEventValid(message)) {
            const stream: VideoStream | undefined = streamsRef.current.find(el => el.kvsName === message.src);
            const converter: EventConverter = new EventConverter(stream);
            const event: VideoStreamEvent = converter.convert(message);
            onMessageRef.current(event);
          }
        };

        if (isMultiCompany) {
          // Multi-company: single company-level subscription per company (1 websocket each)
          effectiveCompanies.forEach((c) => {
            PubSubsManager.instance.subscribeToCompany(c, handler);
          });
        } else {
          // Single company: per-event-type subscriptions (existing behavior)
          effectiveCompanies.forEach((c) => {
            subscribeCompanyToEvents(c, handler);
          });
        }
      } else {
        effectiveCompanies.forEach((c) => {
          if (isMultiCompany) {
            PubSubsManager.instance.unsubscribeFromCompany(c);
          } else {
            PubSubsManager.instance.unsubscribeToCompanyEvents(c);
          }
        });
      }
    }
  }

  function disconectWebsockets(): void {
    const effectiveCompanies = getEffectiveCompanies();
    const isMultiCompany = !companyRef.current;

    effectiveCompanies.forEach((c) => {
      if (isMultiCompany) {
        PubSubsManager.instance.unsubscribeFromCompany(c);
      } else {
        PubSubsManager.instance.unsubscribeToCompanyEvents(c);
      }
      PubSubsManager.instance.unsubscribeToCompanyUpdates(c);
    });

    PubSubsManager.instance.unsubscribeToMutedUpdates()
    PubSubsManager.instance.unsubscribeToEventSelectionUpdates();
  }

  function isEventValid(message: WebsocketVideoStreamEvent): boolean {
    if (message.s && message.e && isEqual(message.s, message.e)) return false;
    return Boolean((message.e && message.e !== 0 && message.s) || (message.ce && message.ce !== 0));
  }

  return null;
}

export default WebsocketConnections;
